import type { RouteDef } from '@/app/routes'
import type { WrittenPage } from '../sections'
import { LINKS } from './links'

/**
 * Digital & AI Maturity Index (DAIMI) — the solution page, rebuilt.
 *
 * It was an **imported** WordPress page until 2026-08-28
 * (`content/imported/pages/en/solutions__digital-ai-maturity-index-daimi.json`,
 * still on disk as the record of what digitopia.co says). The scrape kept the
 * words and lost the page: five unlabelled lists in a row, three customer
 * quotes welded into one paragraph with their attributions attached to the
 * wrong person, and no picture anywhere. Asked for that day with seven
 * screenshots of the live page and ten of its figures — *"DAIMI sayfamızı
 * örneklerdeki gibi tastamam tıpatıp aynısı olacak şekilde tasarla"*.
 *
 * So it is a written page now, band for band the reference's, expressed in this
 * site's own section vocabulary rather than as bespoke markup: the two-column
 * hero, the customer logo row, the anonymous CIO pull quote, the grey
 * "Move beyond traditional consulting." card, the 10x advantage card, the three
 * What's Included cards (Assessment / Strategy / Execution, each with its own
 * platform screen), the Impact Platform card, the report, the course and the
 * resources cards, the three-tab customer quotes and the closing ask.
 *
 * **The copy is digitopia.co's own, verbatim**, taken from the imported JSON
 * rather than transcribed off a screenshot — which is why this page carries far
 * fewer `check` notes than the report pages built the same week. The two things
 * that are not verbatim are named in the checks below: the missing video and
 * three marks we hold no file for.
 *
 * Where this page departs from the reference, and why:
 *
 *  * **The "Move beyond traditional consulting." panel shows a still, not a
 *    video.** The request asks for the uploaded video, embedded and
 *    autoplaying; what arrived was a single frame of it (`videoframe_6257.png`)
 *    and no video file. See the `check` on that section.
 *  * **The closing ask is two buttons, not a nine-field form.** The reference
 *    ends on a HubSpot form; no form on this site submits anywhere yet, so
 *    reproducing it would mean this page's last call to action silently drops
 *    every lead. Same decision, and the same reasoning, as the e-book pages.
 *  * **Three of the ten reference logos are missing** — Gases del Caribe, NKC
 *    and PSDI have no file in `logo-src/`. The row draws the seven we hold.
 */
export const route: RouteDef = {
  id: 'daimiSolution',
  path: 'solutions/digital-ai-maturity-index-daimi',
  priority: 0.9,
}

/**
 * Where every "Try for FREE" button on this page goes. It pointed at the
 * imported DAIMI taster page (`/en/solutions/daimi-taster`) until 2026-08-30;
 * asked that day to send the reader to the Impact Platform instead, so the
 * free trial starts on the product rather than on another marketing page.
 */
const TASTER = 'https://impact.digitopia.ai'

/** The certification course, and the 2026 report's own landing page. */
const COURSE = '/en/digital-ai-maturity-index-daimi-course'
const REPORT = '/en/impact-labs/reports/digital-ai-maturity-report-2026'

/** The Impact Platform itself. The platforms consolidate off this hostname. */
const PLATFORM = 'https://impact.digitopia.ai'

export const daimiSolution: WrittenPage = {
  navLabel: 'Digital & AI Maturity Index',
  title: 'Digital & AI Maturity Index (DAIMI)',
  description:
    'DAIMI is the AI-powered alternative to traditional consulting. We measure your digital and AI maturity, create your roadmap and assist your execution across six dimensions and 120+ topics, against a global benchmark, delivered through the Impact Platform.',
  sections: [
    {
      kind: 'hero',
      eyebrow: 'Digital & AI Maturity Index (DAIMI)',
      headline: 'Grow your business with AI',
      sub: 'We measure your digital and AI maturity. We create your roadmap. We assist your execution.',
      micro: 'Achieve your goals to #transformbetter',
      ctas: [
        // `#contact` rather than the Contact Us page, since 2026-08-30: the
        // closing form now sits at the bottom of every document (see
        // `components/ContactBand.tsx`), so this scrolls the reader down to it.
        { label: 'Talk with an expert', href: '#contact' },
        { label: 'Try for FREE', href: TASTER },
      ],
      photo: {
        src: '/photo/boardroom-daimi-recommendations.webp',
        alt: 'A consultant presenting the Impact Platform’s recommendations and roadmap screen to colleagues around a meeting-room table.',
      },
    },
    {
      kind: 'logos',
      heading: 'Great companies work with Digitopia.',
      items: [
        // The 300x300 transparent squares, not the matted marketing canvases
        // they replaced: `intelai.webp` and `proven.webp` are white-boxed
        // exports with no alpha, so on this band they printed a visible white
        // rectangle beside marks that had none. `padded` sizes a square canvas
        // by width, so the drawn ink matches its neighbours rather than being
        // measured against the empty canvas around it.
        { name: 'Intel', src: '/logo/intelaicard.png', padded: true },
        { name: 'Microsoft', src: '/logo/microsoft.webp' },
        { name: 'PROVEN', src: '/logo/provencard.png', padded: true },
        { name: 'Sempertex', src: '/logo/sempertex.webp' },
        { name: 'Siemens', src: '/logo/siemens.webp' },
        { name: 'Unilever', src: '/logo/unilever.webp' },
        { name: 'Vodafone', src: '/logo/vodafone.webp' },
      ],
      check:
        'The reference row carries ten marks and this one carries seven. Gases '
        + 'del Caribe, NKC of America and PSDI have no file in logo-src/ — they '
        + 'are on no other page of this site either. Send the three marks (tight '
        + 'crop, transparent PNG or WebP) and they drop straight into this row.',
    },
    // The anonymous pull quote the live page opens its argument with. It is the
    // only quote on the page that names no company, which is why it is a plain
    // `quotes` grid of one rather than a Hall of Fame card — those are built
    // around a mark and a face, and this speaker has neither on purpose.
    {
      kind: 'quotes',
      items: [
        {
          quote:
            'Digitopia fundamentally transformed how we view and execute digital transformation. DAIMI gave us quick clarity on where we stood and what to fix first, and the Impact Platform helped us stay aligned, track outcomes, and keep improving.',
          source: 'CIO, Fortune 500 Manufacturing Company',
        },
      ],
    },
    {
      kind: 'cta',
      tone: 'card',
      heading: 'Move beyond traditional consulting.',
      sub: 'DAIMI (Digital and AI Maturity Index) is the AI-powered alternative to traditional consulting. Less time spent on slides, more time spent on decisions and execution.',
      body: [
        'DAIMI is not a framework alone. It is a complete transformation system that combines the best of both worlds: expert guidance and software scale. You get:',
      ],
      points: [
        'Expert consultant to guide alignment and decisions.',
        'A proven maturity framework across six dimensions (people, technology, governance, customer, operations, and innovation).',
        'Global benchmarks to compare performance with peers and leaders.',
        'The Impact Platform, which turns maturity gaps into a prioritized roadmap, keeps teams aligned, and tracks progress and outcomes.',
      ],
      outro: [
        'DAIMI turns maturity insight into a practical roadmap and keeps execution visible, measurable, and on track.',
      ],
      photo: {
        src: '/photo/boardroom-daimi-benchmark-review.png',
        alt: 'A consultant presenting a DAIMI score, the scores by dimension and an industry benchmark on a meeting-room screen.',
      },
      // Uploaded by Fatih through the panel on 2026-08-30. The photograph
      // above stays: it is the poster this panel holds until the film is on
      // screen, and the fallback for a reader who blocks video or asks for
      // reduced motion.
      video: {
        src: 'https://njdxtkezxgkrmwwhzcne.supabase.co/storage/v1/object/public/website-documents/video/9812a580-68e5-4861-b4c5-8df645f64e1e-DMI_Merged_V3_web_crf23.mp4',
        title: 'A walk through DAIMI: the six-dimension maturity framework, the global benchmark and the roadmap it turns into inside the Impact Platform.',
      },
      ctas: [
        // `#contact` rather than the Contact Us page, since 2026-08-30: the
        // closing form now sits at the bottom of every document (see
        // `components/ContactBand.tsx`), so this scrolls the reader down to it.
        { label: 'Talk with an expert', href: '#contact' },
        { label: 'Try for FREE', href: TASTER },
      ],
      check:
        'THE FILM IS HERE, AND IT IS 48 MB. Uploaded on 2026-08-30, which '
        + 'closes a request first made on the 27th. What is left is its weight: '
        + 'this is roughly six times the size worth serving, and every reader who '
        + 'scrolls to this panel downloads all of it — on a phone, on their own '
        + 'data. Nothing is fetched until the panel is actually on screen '
        + '(`components/Video.tsx`), which is what makes 48 MB survivable '
        + 'rather than reckless, but it is not a fix.\n\n'
        + 'Re-encode it and upload the smaller one: H.264, 1080p or 720p, CRF '
        + 'around 23, AAC audio — the same film usually lands between 6 and 10 MB '
        + 'without a visible difference on a page where it plays in a column about '
        + '400px wide. Then swap the URL on this section; nothing else changes. '
        + 'Worth doing before this page goes to production, not after.',
    },
    {
      kind: 'cta',
      tone: 'card',
      flip: true,
      heading: 'Your Unfair 10x Advantage',
      points: [
        '10x Faster Results: Assess your maturity index in hours, not months. No endless interviews or waiting forever for your results.',
        '10x Bigger Impact: Get immediate, data-driven alignment and a concrete roadmap that delivers tangible business value from day one.',
        '10x More Affordable: We introduce consulting as a software (CaaS). No time and material, no hidden consulting hours, and no strings attached.',
        '10x More Efficient: Assure your success and control your progress, not with dozens of consultants but with an efficient AI-powered platform.',
      ],
      photo: {
        src: '/photo/boardroom-daimi-target-state-applause.webp',
        alt: 'A leadership team applauding in front of a screen showing their DAIMI current and target state.',
      },
      ctas: [
        // `#contact` rather than the Contact Us page, since 2026-08-30: the
        // closing form now sits at the bottom of every document (see
        // `components/ContactBand.tsx`), so this scrolls the reader down to it.
        { label: 'Talk with an expert', href: '#contact' },
        { label: 'Try for FREE', href: TASTER },
      ],
    },
    // "What's Included" and the three things it is a list of. A centred
    // standfirst draws no grey band, so the reader meets the question and the
    // three answers under it as one block.
    {
      kind: 'prose',
      align: 'center',
      size: 'lg',
      heading: 'What’s Included',
      sub: 'You get a unified solution, ecosystem, and platform to support your organization across assessment, strategy, execution, and continuous improvement.',
    },
    {
      kind: 'cta',
      tone: 'card',
      flip: true,
      heading: 'Assessment',
      points: [
        'DAIMI Framework, consists of 6 dimensions and 120+ topics.',
        '6 workshops with your teams, moderated by a certified expert.',
        'DAIMI Scorecard including industry benchmark.',
        'Vitality and Snowflake Analysis to deeply understand your situation.',
        'Executive briefing to your top management.',
      ],
      photo: {
        src: '/photo/impact-platform-people-session.webp',
        alt: 'The Impact Platform’s People session: the topic list on the left and the current and target scores for Agility on the right.',
      },
      ctas: [
        // `#contact` rather than the Contact Us page, since 2026-08-30: the
        // closing form now sits at the bottom of every document (see
        // `components/ContactBand.tsx`), so this scrolls the reader down to it.
        { label: 'Talk with an expert', href: '#contact' },
        { label: 'Try for FREE', href: TASTER },
      ],
    },
    {
      kind: 'cta',
      tone: 'card',
      heading: 'Strategy',
      points: [
        'Workshops and tools to determine your business goals and priorities.',
        'Workshops and tools to determine your investments, projects, and your roadmap.',
        'Executive briefing to your top management for effective decision making.',
      ],
      photo: {
        src: '/photo/impact-platform-strategy-projects.webp',
        alt: 'The Impact Platform’s Projects screen: candidate projects plotted by urgency and importance beside the list they came from.',
      },
      ctas: [
        // `#contact` rather than the Contact Us page, since 2026-08-30: the
        // closing form now sits at the bottom of every document (see
        // `components/ContactBand.tsx`), so this scrolls the reader down to it.
        { label: 'Talk with an expert', href: '#contact' },
        { label: 'Try for FREE', href: TASTER },
      ],
    },
    {
      kind: 'cta',
      tone: 'card',
      flip: true,
      heading: 'Execution',
      points: [
        'Transformation management office software platform (soon)',
        '3 Quarterly Business Review and Quality Assurance',
        'Ongoing support with AI wizards (soon)',
      ],
      photo: {
        src: '/photo/impact-platform-execution-roadmap.webp',
        alt: 'The Impact Platform’s Roadmap screen: three programmes of projects laid out across four quarters.',
      },
      ctas: [
        // `#contact` rather than the Contact Us page, since 2026-08-30: the
        // closing form now sits at the bottom of every document (see
        // `components/ContactBand.tsx`), so this scrolls the reader down to it.
        { label: 'Talk with an expert', href: '#contact' },
        { label: 'Try for FREE', href: TASTER },
      ],
    },
    {
      kind: 'cta',
      tone: 'card',
      heading: 'Impact Platform',
      body: [
        'DAIMI is delivered through the Impact Platform, the transformation management software that turns maturity insights into measurable execution. In one place, you can benchmark against peers, translate findings into a prioritized roadmap, assign ownership, and track progress and impact with analytics and AI-powered guidance, so your transformation moves from assessment to outcomes without the delays of traditional approaches.',
      ],
      photo: {
        src: '/photo/impact-platform-daimi-dashboard.webp',
        alt: 'The Impact Platform home screen: the current DAIMI score, annual progress, milestones and the dimension runs in flight.',
      },
      ctas: [{ label: 'Learn more about the platform', href: PLATFORM }],
    },
    {
      kind: 'cta',
      tone: 'card',
      flip: true,
      heading: 'Digital & AI Maturity Report 2026',
      body: [
        'AI adoption is accelerating, but true capability is lagging. Based on six years of research and over 1,300 assessments across 20+ countries, the Digital & AI Maturity Report 2026 provides the global reality check leaders need. With data from 1,575 organizations, this report quantifies the ‘capability gap’ and offers an evidence-based North Star to help you replace assumptions with decisive action.',
      ],
      photo: {
        src: '/photo/daimi-report-2026-mockup.webp',
        alt: 'Two copies of the Digital & AI Maturity Report 2026, February 2026.',
      },
      ctas: [{ label: 'Download Report', href: REPORT }],
    },
    {
      kind: 'cta',
      tone: 'card',
      heading: 'Get Certified as an Expert',
      body: [
        'The popular, structured, expert-led course is designed to help you develop the skills and practical knowledge needed for digital transformation. Earn a recognized certificate.',
      ],
      photo: {
        src: '/photo/learning-platform-tablet.webp',
        alt: 'The Digitopia Learning Platform sign-in screen on a tablet.',
      },
      ctas: [{ label: 'Start Learning', href: COURSE }],
    },
    {
      kind: 'cta',
      tone: 'card',
      flip: true,
      heading: 'Stay Ahead with the Right Knowledge',
      body: [
        'Access the latest strategic guides to make informed decisions and accelerate your transformation journey. Stay ahead with exclusive insights.',
      ],
      photo: {
        src: '/photo/digitopia-report-and-ebook-covers.webp',
        alt: 'Five Digitopia covers fanned out: the Digital & AI Maturity Report 2026, Top 10 Trends 2026 and three e-books.',
      },
      ctas: [{ label: 'Explore Resources', href: LINKS.reports }],
    },
    // The three customer quotes, in the Hall of Fame card with a tab per
    // company — asked for on 2026-08-28 (*"kaydırmalı testimonialda proven
    // dışındaki 2 seçenek"*), with Yazaki's and Kordsa's words supplied in the
    // message. They are the same words, the same marks and the same portraits
    // `content/testimonials.ts` already carries for those three companies, so
    // the page and the Hall of Fame cannot drift apart.
    {
      kind: 'quotes',
      variant: 'fame',
      items: [
        {
          quote:
            'Working with Digitopia has been a transformative experience for PROVEN. The Digital Maturity Index provided the clarity and structure we needed to assess our capabilities, while the interactive sessions delivered valuable, actionable insights. Benchmarking against industry peers gave us clear direction, ensuring smarter investments and stronger alignment across leadership. This initiative has set a solid foundation for our digital transformation, and we’re excited to build on this momentum.',
          source: 'Johann Heaven, CEO, PROVEN',
          company: 'PROVEN',
          logo: { name: 'PROVEN', src: '/logo/provencard.png' },
          portrait: { src: '/photo/portrait-johann-heaven.png', alt: 'Johann Heaven' },
          cta: { label: 'Read PROVEN’s story', href: '/en/customers/proven' },
        },
        {
          quote:
            'Digitopia’s Digital Maturity Index gave us the clarity about the positive progress we have made recently in creating business value with digitalization. We also had a chance to benchmark ourselves with the industry peers. Digitopia’s insightful recommendations helped us fine tune and confirm our strategy. This partnership has been a key milestone in our journey to innovation and digitalization.',
          source: 'Tekin Gülşen, VP of IT Y-EMEA and Global Applications',
          company: 'Yazaki',
          logo: { name: 'Yazaki', src: '/logo/yazakicard.png' },
          portrait: { src: '/photo/portrait-tekin-gulsen.png', alt: 'Tekin Gülşen' },
          cta: { label: 'Read Yazaki’s story', href: '/en/customers/yazaki' },
        },
        {
          quote:
            'Digitopia’s DMI assessments have helped us create clear, actionable digital roadmaps while staying on track. Their insights have been invaluable, guiding us to focus on critical areas like AI long before it became a major topic. As part of Sabancı Holding, it’s a significant advantage that other group companies also use Digitopia’s assessments, enabling a comprehensive view of digital maturity across the group. Digitopia is not just a partner; they play an essential role in shaping and driving our digital strategy.',
          source: 'Fatih Akar, Director of Global Information Technologies',
          company: 'Kordsa',
          logo: { name: 'Kordsa', src: '/logo/kordsacard.png' },
          portrait: { src: '/photo/portrait-fatih-akar.png', alt: 'Fatih Akar' },
          cta: { label: 'Read Kordsa’s story', href: '/en/customers/kordsa' },
        },
      ],
      cta: { label: 'Read the full stories', href: LINKS.hallOfFame },
    },
    /*
      No closing dark CTA band here, asked for 2026-08-30: "form olan herhangi
      bir sayfada formun hemen üstünde genelde bu siyah kutu oluyor, bunlar
      olmasın". The closing form (`components/ContactBand.tsx`) already sits at
      the foot of this page, so a band whose button pointed at `#contact` was
      asking the reader to scroll to something they could already see. The
      "world's leading" superlative that used to carry a `check:` here went with
      it — it is no longer on the page to confirm.
    */
  ],
}
